import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, AreaChart, Area
} from 'recharts';
import { 
  UserPlus, UserMinus, Plus, RefreshCw, TrendingUp, TrendingDown, 
  Activity, Calendar, Users, Loader2, ArrowUpRight, ArrowDownRight,
  Upload, Download, FileSpreadsheet, CheckCircle2, XCircle, AlertCircle,
  Trash2, Save, ClipboardList, Clock, Target, History, ChevronDown,
  GitCompare, ArrowRight, Minus, Equal, FileDown
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';
import { EnhancedReportSection } from '@/components/EnhancedReportSection';

// Form Field Component
const FormField = ({ label, children, hint, required }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium flex items-center gap-1">
      {label}
      {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

// Form Section Component
const FormSection = ({ title, description, icon: Icon, children }) => (
  <div className="form-section animate-fade-in">
    <div className="mb-6 flex items-start gap-3">
      {Icon && (
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      )}
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        {description && <p className="text-sm text-muted-foreground mt-0.5">{description}</p>}
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      {children}
    </div>
  </div>
);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TEAMS = ['All', 'Blue', 'Yellow', 'Green'];

export default function AdmissionsDischarges() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [records, setRecords] = useState([]);
  const [trends, setTrends] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState('All');
  
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // CSV Import state
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [parsedRecords, setParsedRecords] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  
  // Delete state
  const [deletingId, setDeletingId] = useState(null);
  
  // Records filter state
  const [recordsFilterTeam, setRecordsFilterTeam] = useState('All');
  const [recordsFilterStartDate, setRecordsFilterStartDate] = useState('');
  const [recordsFilterEndDate, setRecordsFilterEndDate] = useState('');
  
  // Program Overview state
  const [savingProgram, setSavingProgram] = useState(false);
  const [loadingPeriodData, setLoadingPeriodData] = useState(false);
  const [existingRecordId, setExistingRecordId] = useState(null);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [periodHistory, setPeriodHistory] = useState([]); // All available periods with data
  const [periodType, setPeriodType] = useState('weekly');
  const [periodEndDate, setPeriodEndDate] = useState(() => {
    // Default to end of current week (Sunday)
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilSunday = dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
    const endOfWeek = new Date(today);
    endOfWeek.setDate(today.getDate() + daysUntilSunday);
    return endOfWeek.toISOString().split('T')[0];
  });
  
  // Compare Periods state
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [comparePeriod1, setComparePeriod1] = useState(null);
  const [comparePeriod2, setComparePeriod2] = useState(null);
  
  const [programData, setProgramData] = useState({
    adc: '', total_admissions: '', live_discharges_percent: '', revocations_percent: '',
    alos: '', cahps_overall_score: '', open_surveys: '', resolved_surveys: '',
    open_complaints: '', resolved_complaints: '',
    // Census & Operations Metrics
    current_census: '',
    conversion_ratio: '',
    weekly_discharges: '',
    median_length_of_stay: '',
    net_census_change: '',
    // Pipeline Metrics
    pending_referrals: '',
    same_day_admits: '',
    insurance_pending: '',
    total_referrals: ''
  });
  
  const [newRecord, setNewRecord] = useState({
    period_start: new Date().toISOString().split('T')[0],
    period_end: new Date().toISOString().split('T')[0],
    admissions: 0,
    discharges: 0,
    deaths: 0,
    revocations: 0,
    transfers_in: 0,
    transfers_out: 0,
    starting_census: 50,
    team: '',
    notes: ''
  });

  const fetchData = async () => {
    try {
      const teamParam = selectedTeam !== 'All' ? `&team=${selectedTeam}` : '';
      const [recordsRes, trendsRes, programRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/census/admissions-discharges?limit=20${teamParam}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/census/trends?periods=12`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/metrics/program-overview/latest`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/metrics/program-overview?limit=50`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (recordsRes.ok) setRecords(await recordsRes.json());
      if (trendsRes.ok) setTrends(await trendsRes.json());
      
      // Build period history from all records
      if (historyRes.ok) {
        const allRecords = await historyRes.json();
        // Group by period_date and keep latest entry for each
        const periodMap = new Map();
        allRecords.forEach(record => {
          const key = `${record.period_type || 'weekly'}_${record.period_date}`;
          if (!periodMap.has(key) || new Date(record.created_at) > new Date(periodMap.get(key).created_at)) {
            periodMap.set(key, record);
          }
        });
        // Convert to array and sort by date descending
        const uniquePeriods = Array.from(periodMap.values())
          .filter(r => r.period_date) // Only include records with period_date
          .sort((a, b) => new Date(b.period_date) - new Date(a.period_date))
          .slice(0, 12); // Keep last 12 periods
        setPeriodHistory(uniquePeriods);
      }
      
      if (programRes.ok) {
        const data = await programRes.json();
        if (data && Object.keys(data).length > 0) {
          setProgramData({
            adc: data.adc?.toString() || '',
            total_admissions: data.total_admissions?.toString() || '',
            live_discharges_percent: data.live_discharges_percent?.toString() || '',
            revocations_percent: data.revocations_percent?.toString() || '',
            alos: data.alos?.toString() || '',
            cahps_overall_score: data.cahps_overall_score?.toString() || '',
            open_surveys: data.open_surveys?.toString() || '',
            resolved_surveys: data.resolved_surveys?.toString() || '',
            open_complaints: data.open_complaints?.toString() || '',
            resolved_complaints: data.resolved_complaints?.toString() || '',
            current_census: data.current_census?.toString() || '',
            conversion_ratio: data.conversion_ratio?.toString() || '',
            weekly_discharges: data.weekly_discharges?.toString() || '',
            median_length_of_stay: data.median_length_of_stay?.toString() || '',
            net_census_change: data.net_census_change?.toString() || '',
            pending_referrals: data.pending_referrals?.toString() || '',
            same_day_admits: data.same_day_admits?.toString() || '',
            insurance_pending: data.insurance_pending?.toString() || '',
            total_referrals: data.total_referrals?.toString() || ''
          });
          // Set editing state if we loaded latest data
          if (data.id) {
            setExistingRecordId(data.id);
            setIsEditingExisting(true);
            if (data.period_date) {
              setPeriodEndDate(data.period_date);
            }
            if (data.period_type) {
              setPeriodType(data.period_type);
            }
          }
        }
      }
    } catch (error) {
      toast.error('Failed to load census data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token, selectedTeam]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
    toast.success('Data refreshed');
  };

  // Load historical data for a specific period
  const loadPeriodData = async (date, type) => {
    setLoadingPeriodData(true);
    try {
      const response = await fetch(
        `${API_URL}/api/metrics/program-overview?period_type=${type}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.ok) {
        const allRecords = await response.json();
        // Find record matching the selected date
        const matchingRecord = allRecords.find(r => r.period_date === date);
        
        if (matchingRecord) {
          // Found existing data - populate form
          setProgramData({
            adc: matchingRecord.adc?.toString() || '',
            total_admissions: matchingRecord.total_admissions?.toString() || '',
            live_discharges_percent: matchingRecord.live_discharges_percent?.toString() || '',
            revocations_percent: matchingRecord.revocations_percent?.toString() || '',
            alos: matchingRecord.alos?.toString() || '',
            cahps_overall_score: matchingRecord.cahps_overall_score?.toString() || '',
            open_surveys: matchingRecord.open_surveys?.toString() || '',
            resolved_surveys: matchingRecord.resolved_surveys?.toString() || '',
            open_complaints: matchingRecord.open_complaints?.toString() || '',
            resolved_complaints: matchingRecord.resolved_complaints?.toString() || '',
            current_census: matchingRecord.current_census?.toString() || '',
            conversion_ratio: matchingRecord.conversion_ratio?.toString() || '',
            weekly_discharges: matchingRecord.weekly_discharges?.toString() || '',
            median_length_of_stay: matchingRecord.median_length_of_stay?.toString() || '',
            net_census_change: matchingRecord.net_census_change?.toString() || '',
            pending_referrals: matchingRecord.pending_referrals?.toString() || '',
            same_day_admits: matchingRecord.same_day_admits?.toString() || '',
            insurance_pending: matchingRecord.insurance_pending?.toString() || '',
            total_referrals: matchingRecord.total_referrals?.toString() || ''
          });
          setExistingRecordId(matchingRecord.id);
          setIsEditingExisting(true);
          toast.info('Loaded existing data for this period', {
            description: `Last updated: ${new Date(matchingRecord.created_at).toLocaleString()}`
          });
        } else {
          // No data for this period - clear form for new entry
          setProgramData({
            adc: '', total_admissions: '', live_discharges_percent: '', revocations_percent: '',
            alos: '', cahps_overall_score: '', open_surveys: '', resolved_surveys: '',
            open_complaints: '', resolved_complaints: '', current_census: '',
            conversion_ratio: '', weekly_discharges: '', median_length_of_stay: '',
            net_census_change: '', pending_referrals: '', same_day_admits: '',
            insurance_pending: '', total_referrals: ''
          });
          setExistingRecordId(null);
          setIsEditingExisting(false);
          toast.info('No data found for this period', {
            description: 'Enter new metrics for this reporting period.'
          });
        }
      }
    } catch (error) {
      console.error('Failed to load period data:', error);
    } finally {
      setLoadingPeriodData(false);
    }
  };

  // Handle period date change
  const handlePeriodDateChange = (newDate) => {
    setPeriodEndDate(newDate);
    loadPeriodData(newDate, periodType);
  };

  // Handle period type change
  const handlePeriodTypeChange = (newType) => {
    setPeriodType(newType);
    loadPeriodData(periodEndDate, newType);
  };

  // Handle selecting a period from history dropdown
  const handleSelectHistoryPeriod = (record) => {
    setPeriodType(record.period_type || 'weekly');
    setPeriodEndDate(record.period_date);
    // Directly populate form with the selected record's data
    setProgramData({
      adc: record.adc?.toString() || '',
      total_admissions: record.total_admissions?.toString() || '',
      live_discharges_percent: record.live_discharges_percent?.toString() || '',
      revocations_percent: record.revocations_percent?.toString() || '',
      alos: record.alos?.toString() || '',
      cahps_overall_score: record.cahps_overall_score?.toString() || '',
      open_surveys: record.open_surveys?.toString() || '',
      resolved_surveys: record.resolved_surveys?.toString() || '',
      open_complaints: record.open_complaints?.toString() || '',
      resolved_complaints: record.resolved_complaints?.toString() || '',
      current_census: record.current_census?.toString() || '',
      conversion_ratio: record.conversion_ratio?.toString() || '',
      weekly_discharges: record.weekly_discharges?.toString() || '',
      median_length_of_stay: record.median_length_of_stay?.toString() || '',
      net_census_change: record.net_census_change?.toString() || '',
      pending_referrals: record.pending_referrals?.toString() || '',
      same_day_admits: record.same_day_admits?.toString() || '',
      insurance_pending: record.insurance_pending?.toString() || '',
      total_referrals: record.total_referrals?.toString() || ''
    });
    setExistingRecordId(record.id);
    setIsEditingExisting(true);
    toast.success(`Loaded ${record.period_type || 'weekly'} data for ${new Date(record.period_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, {
      description: `ADC: ${record.adc}, Admissions: ${record.total_admissions}`
    });
  };

  // Format period for display
  const formatPeriodLabel = (record) => {
    const date = new Date(record.period_date);
    const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const type = record.period_type === 'monthly' ? 'Month' : 'Week';
    return `${type} ${formatted}`;
  };

  // Compare periods helper - calculate difference
  const calculateDiff = (val1, val2) => {
    const num1 = parseFloat(val1) || 0;
    const num2 = parseFloat(val2) || 0;
    return num1 - num2;
  };

  // Get comparison metrics config
  const comparisonMetrics = [
    { key: 'adc', label: 'Average Daily Census', suffix: '', decimals: 1 },
    { key: 'current_census', label: 'Current Census', suffix: '', decimals: 0 },
    { key: 'total_admissions', label: 'Total Admissions', suffix: '', decimals: 0 },
    { key: 'weekly_discharges', label: 'Weekly Discharges', suffix: '', decimals: 0 },
    { key: 'net_census_change', label: 'Net Census Change', suffix: '', decimals: 0 },
    { key: 'live_discharges_percent', label: 'Live Discharges', suffix: '%', decimals: 1 },
    { key: 'revocations_percent', label: 'Revocations', suffix: '%', decimals: 1 },
    { key: 'alos', label: 'Avg Length of Stay', suffix: ' days', decimals: 1 },
    { key: 'cahps_overall_score', label: 'CAHPS Score', suffix: '%', decimals: 1 },
    { key: 'conversion_ratio', label: 'Conversion Ratio', suffix: '%', decimals: 1 },
    { key: 'total_referrals', label: 'Total Referrals', suffix: '', decimals: 0 },
    { key: 'pending_referrals', label: 'Pending Referrals', suffix: '', decimals: 0 },
  ];

  // Open compare dialog with defaults
  const openCompareDialog = () => {
    if (periodHistory.length >= 2) {
      setComparePeriod1(periodHistory[0]); // Most recent
      setComparePeriod2(periodHistory[1]); // Second most recent
    }
    setShowCompareDialog(true);
  };

  // Export comparison as CSV
  const exportComparisonCSV = () => {
    if (!comparePeriod1 || !comparePeriod2) return;
    
    const headers = ['Metric', formatPeriodLabel(comparePeriod1), 'Change', formatPeriodLabel(comparePeriod2)];
    const rows = comparisonMetrics.map(metric => {
      const val1 = parseFloat(comparePeriod1[metric.key]) || 0;
      const val2 = parseFloat(comparePeriod2[metric.key]) || 0;
      const diff = val2 - val1;
      const changeStr = diff === 0 ? '0' : (diff > 0 ? `+${diff.toFixed(metric.decimals)}` : diff.toFixed(metric.decimals));
      return [
        metric.label,
        `${val1.toFixed(metric.decimals)}${metric.suffix}`,
        `${changeStr}${metric.suffix}`,
        `${val2.toFixed(metric.decimals)}${metric.suffix}`
      ];
    });
    
    // Calculate summary
    const improved = comparisonMetrics.filter(m => {
      const diff = (parseFloat(comparePeriod2[m.key]) || 0) - (parseFloat(comparePeriod1[m.key]) || 0);
      const invertedMetrics = ['live_discharges_percent', 'revocations_percent', 'pending_referrals', 'open_complaints'];
      return invertedMetrics.includes(m.key) ? diff < 0 : diff > 0;
    }).length;
    const declined = comparisonMetrics.filter(m => {
      const diff = (parseFloat(comparePeriod2[m.key]) || 0) - (parseFloat(comparePeriod1[m.key]) || 0);
      const invertedMetrics = ['live_discharges_percent', 'revocations_percent', 'pending_referrals', 'open_complaints'];
      return invertedMetrics.includes(m.key) ? diff > 0 : diff < 0;
    }).length;
    const unchanged = comparisonMetrics.length - improved - declined;
    
    // Build CSV
    let csv = 'Period Comparison Report\n';
    csv += `Generated: ${new Date().toLocaleString()}\n`;
    csv += `\nBaseline: ${formatPeriodLabel(comparePeriod1)} (${comparePeriod1.period_date})\n`;
    csv += `Compare To: ${formatPeriodLabel(comparePeriod2)} (${comparePeriod2.period_date})\n\n`;
    csv += `Summary: ${improved} Improved | ${unchanged} Unchanged | ${declined} Declined\n\n`;
    csv += headers.join(',') + '\n';
    csv += rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    
    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `period_comparison_${comparePeriod1.period_date}_vs_${comparePeriod2.period_date}.csv`;
    link.click();
    
    toast.success('Comparison exported', {
      description: 'CSV file downloaded successfully'
    });
  };

  // Save Program Overview Data
  const saveProgramData = async () => {
    setSavingProgram(true);
    
    const processedData = { ...programData };
    Object.keys(processedData).forEach(key => {
      const val = processedData[key];
      if (val === '') {
        processedData[key] = 0;
      } else if (!isNaN(parseFloat(val))) {
        processedData[key] = parseFloat(val);
      }
    });

    try {
      const response = await fetch(`${API_URL}/api/metrics/program-overview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...processedData,
          period_type: periodType,
          period_date: periodEndDate,
          existing_record_id: existingRecordId // For tracking updates
        })
      });

      if (response.ok) {
        const savedData = await response.json();
        setExistingRecordId(savedData.id);
        setIsEditingExisting(true);
        toast.success(isEditingExisting ? 'Period data updated successfully' : 'Program Overview data saved successfully', {
          description: 'All dashboards and strategic planning pages will now reflect these updated metrics.'
        });
        // Refresh data to update summary cards
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to save', { description: error.detail || 'Unknown error' });
      }
    } catch (error) {
      toast.error('Connection error', { description: 'Unable to save data' });
    } finally {
      setSavingProgram(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/census/admissions-discharges`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(newRecord)
      });

      if (response.ok) {
        toast.success('Census record added');
        setShowAddDialog(false);
        setNewRecord({
          period_start: new Date().toISOString().split('T')[0],
          period_end: new Date().toISOString().split('T')[0],
          admissions: 0,
          discharges: 0,
          deaths: 0,
          revocations: 0,
          transfers_in: 0,
          transfers_out: 0,
          starting_census: records.length > 0 ? records[0].ending_census || 50 : 50,
          team: '',
          notes: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to add record', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error adding record');
    } finally {
      setSubmitting(false);
    }
  };

  // CSV Import functions
  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const records = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length < headers.length) continue;
      
      const record = {};
      headers.forEach((header, idx) => {
        record[header] = values[idx] || '';
      });
      records.push(record);
    }
    
    return records;
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    setImportFile(file);
    setImportResult(null);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const records = parseCSV(text);
      setParsedRecords(records);
      
      if (records.length === 0) {
        toast.error('No valid records found in CSV');
      } else {
        toast.success(`Parsed ${records.length} records from CSV`);
      }
    };
    reader.readAsText(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/census/csv-template`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const blob = new Blob([data.template], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'census_import_template.csv';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Template downloaded');
      }
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (parsedRecords.length === 0) {
      toast.error('No records to import');
      return;
    }
    
    setImporting(true);
    try {
      const response = await fetch(`${API_URL}/api/census/import-csv`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ records: parsedRecords, overwrite: true })
      });
      
      if (response.ok) {
        const result = await response.json();
        setImportResult(result);
        
        if (result.imported > 0) {
          toast.success(`Successfully imported ${result.imported} records`);
          fetchData();
        }
        
        if (result.errors.length > 0) {
          toast.warning(`${result.errors.length} records had errors`);
        }
      } else {
        const error = await response.json();
        toast.error('Import failed', { description: error.detail });
      }
    } catch (error) {
      toast.error('Error during import');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setParsedRecords([]);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Delete record function
  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this census record? This action cannot be undone.')) {
      return;
    }
    
    setDeletingId(recordId);
    try {
      const response = await fetch(`${API_URL}/api/census/admissions-discharges/${recordId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Census record deleted successfully');
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to delete record', { description: error.detail || 'Unknown error' });
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Error deleting record', { description: error.message });
    } finally {
      setDeletingId(null);
    }
  };

  // Delete all records function
  const [deletingAll, setDeletingAll] = useState(false);
  
  const handleDeleteAll = async () => {
    if (records.length === 0) {
      toast.info('No records to delete');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete ALL ${records.length} census records? This action cannot be undone.`)) {
      return;
    }
    
    setDeletingAll(true);
    
    try {
      const response = await fetch(`${API_URL}/api/census/admissions-discharges/bulk/all`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(`Deleted ${result.deleted_count} records`);
        fetchData();
      } else {
        const error = await response.json();
        toast.error('Failed to delete records', { description: error.detail || 'Unknown error' });
      }
    } catch (error) {
      console.error('Delete all error:', error);
      toast.error('Error deleting records', { description: error.message });
    } finally {
      setDeletingAll(false);
    }
  };

  // Calculate summary stats
  const totalAdmissions = records.reduce((s, r) => s + (r.admissions || 0), 0);
  const totalDischarges = records.reduce((s, r) => s + (r.discharges || 0), 0);
  const avgWeeklyAdmissions = records.length > 0 ? (totalAdmissions / records.length).toFixed(1) : 0;
  const avgWeeklyDischarges = records.length > 0 ? (totalDischarges / records.length).toFixed(1) : 0;
  const netChange = totalAdmissions - totalDischarges - records.reduce((s, r) => s + (r.deaths || 0) + (r.revocations || 0), 0);

  // Prepare chart data
  const chartData = [...(trends?.admissions_discharges || [])].map(r => ({
    date: r.period_start,
    admissions: r.admissions,
    discharges: r.discharges,
    deaths: r.deaths,
    net: r.net_change,
    census: r.ending_census
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-[#00b0ab] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admissions-discharges-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[Manrope] bg-gradient-to-r from-[#00b0ab] to-[#008a86] bg-clip-text text-transparent">
            Admissions & Discharges
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Track census changes for capacity planning
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedTeam} onValueChange={setSelectedTeam}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEAMS.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          {/* Import CSV Dialog */}
          <Dialog open={showImportDialog} onOpenChange={(open) => { setShowImportDialog(open); if (!open) resetImport(); }}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="import-csv-btn">
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="h-5 w-5" />
                  Import Census Data from CSV
                </DialogTitle>
                <DialogDescription>
                  Upload a CSV file with historical census data. Existing records will be overwritten.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Download Template */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                  <div>
                    <p className="text-sm font-medium">Need a template?</p>
                    <p className="text-xs text-muted-foreground">Download our CSV template with the correct column format</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={downloadTemplate}>
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                </div>
                
                {/* Drag & Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    dragActive 
                      ? 'border-[#00b0ab] bg-[#00b0ab]/10 dark:bg-[#00b0ab]/10' 
                      : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={(e) => handleFileSelect(e.target.files[0])}
                  />
                  
                  {!importFile ? (
                    <>
                      <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                      <p className="text-sm text-muted-foreground mb-2">
                        Drag and drop your CSV file here, or
                      </p>
                      <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                        Browse Files
                      </Button>
                    </>
                  ) : (
                    <div className="flex items-center justify-center gap-3">
                      <FileSpreadsheet className="h-8 w-8 text-emerald-500" />
                      <div className="text-left">
                        <p className="font-medium">{importFile.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {parsedRecords.length} records parsed
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={resetImport}>
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Preview Table */}
                {parsedRecords.length > 0 && !importResult && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Preview (first 5 records):</p>
                    <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50 dark:bg-slate-900">
                            <TableHead className="text-xs">Period</TableHead>
                            <TableHead className="text-xs">Start Census</TableHead>
                            <TableHead className="text-xs">Admits</TableHead>
                            <TableHead className="text-xs">Disch</TableHead>
                            <TableHead className="text-xs">Deaths</TableHead>
                            <TableHead className="text-xs">Team</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {parsedRecords.slice(0, 5).map((r, i) => (
                            <TableRow key={i}>
                              <TableCell className="text-xs">{r.period_start}</TableCell>
                              <TableCell className="text-xs">{r.starting_census || 50}</TableCell>
                              <TableCell className="text-xs">{r.admissions || 0}</TableCell>
                              <TableCell className="text-xs">{r.discharges || 0}</TableCell>
                              <TableCell className="text-xs">{r.deaths || 0}</TableCell>
                              <TableCell className="text-xs">{r.team || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                    {parsedRecords.length > 5 && (
                      <p className="text-xs text-muted-foreground text-center">
                        ...and {parsedRecords.length - 5} more records
                      </p>
                    )}
                  </div>
                )}
                
                {/* Import Result */}
                {importResult && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950 text-center">
                        <CheckCircle2 className="h-6 w-6 mx-auto text-emerald-500 mb-1" />
                        <p className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{importResult.imported}</p>
                        <p className="text-xs text-emerald-600">Imported</p>
                      </div>
                      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950 text-center">
                        <AlertCircle className="h-6 w-6 mx-auto text-amber-500 mb-1" />
                        <p className="text-lg font-bold text-amber-700 dark:text-amber-300">{importResult.skipped}</p>
                        <p className="text-xs text-amber-600">Skipped</p>
                      </div>
                      <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950 text-center">
                        <XCircle className="h-6 w-6 mx-auto text-red-500 mb-1" />
                        <p className="text-lg font-bold text-red-700 dark:text-red-300">{importResult.errors.length}</p>
                        <p className="text-xs text-red-600">Errors</p>
                      </div>
                    </div>
                    
                    {importResult.errors.length > 0 && (
                      <div className="p-3 rounded-lg bg-red-50 dark:bg-red-950">
                        <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">Errors:</p>
                        <ul className="text-xs text-red-600 space-y-1 max-h-24 overflow-y-auto">
                          {importResult.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowImportDialog(false); resetImport(); }}>
                  {importResult ? 'Close' : 'Cancel'}
                </Button>
                {!importResult && (
                  <Button 
                    onClick={handleImport} 
                    disabled={parsedRecords.length === 0 || importing}
                    className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Import {parsedRecords.length} Records
                      </>
                    )}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-gradient-to-r from-[#00b0ab] to-[#008a86]" data-testid="add-census-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Record
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Add Census Record</DialogTitle>
                <DialogDescription>Enter weekly admissions and discharges</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Period Start</Label>
                    <Input 
                      type="date"
                      value={newRecord.period_start}
                      onChange={(e) => setNewRecord({...newRecord, period_start: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Period End</Label>
                    <Input 
                      type="date"
                      value={newRecord.period_end}
                      onChange={(e) => setNewRecord({...newRecord, period_end: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Starting Census</Label>
                    <Input 
                      type="number"
                      value={newRecord.starting_census}
                      onChange={(e) => setNewRecord({...newRecord, starting_census: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <UserPlus className="h-4 w-4 text-emerald-500" /> Admissions
                    </Label>
                    <Input 
                      type="number"
                      value={newRecord.admissions}
                      onChange={(e) => setNewRecord({...newRecord, admissions: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <UserMinus className="h-4 w-4 text-amber-500" /> Discharges
                    </Label>
                    <Input 
                      type="number"
                      value={newRecord.discharges}
                      onChange={(e) => setNewRecord({...newRecord, discharges: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Deaths</Label>
                    <Input 
                      type="number"
                      value={newRecord.deaths}
                      onChange={(e) => setNewRecord({...newRecord, deaths: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Revocations</Label>
                    <Input 
                      type="number"
                      value={newRecord.revocations}
                      onChange={(e) => setNewRecord({...newRecord, revocations: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Transfers In</Label>
                    <Input 
                      type="number"
                      value={newRecord.transfers_in}
                      onChange={(e) => setNewRecord({...newRecord, transfers_in: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Transfers Out</Label>
                    <Input 
                      type="number"
                      value={newRecord.transfers_out}
                      onChange={(e) => setNewRecord({...newRecord, transfers_out: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Team (Optional)</Label>
                    <Select value={newRecord.team} onValueChange={(v) => setNewRecord({...newRecord, team: v})}>
                      <SelectTrigger><SelectValue placeholder="Select team" /></SelectTrigger>
                      <SelectContent>
                        {TEAMS.filter(t => t !== 'All').map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Input 
                      value={newRecord.notes}
                      onChange={(e) => setNewRecord({...newRecord, notes: e.target.value})}
                      placeholder="Optional notes"
                    />
                  </div>
                </div>
                
                {/* Preview */}
                <div className="mt-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-900">
                  <p className="text-sm font-medium mb-2">Preview:</p>
                  <p className="text-sm text-muted-foreground">
                    Starting Census: {newRecord.starting_census} → 
                    Ending Census: {newRecord.starting_census + newRecord.admissions + newRecord.transfers_in - newRecord.discharges - newRecord.deaths - newRecord.revocations - newRecord.transfers_out}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Net Change: {newRecord.admissions + newRecord.transfers_in - newRecord.discharges - newRecord.deaths - newRecord.revocations - newRecord.transfers_out}
                  </p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Add Record
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 border-emerald-200 dark:border-emerald-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Weekly Admissions</p>
                <div className="flex items-center gap-2">
                  <p className="text-3xl font-bold">{avgWeeklyAdmissions}</p>
                  <ArrowUpRight className="h-5 w-5 text-emerald-500" />
                </div>
              </div>
              <UserPlus className="h-10 w-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-200 dark:border-amber-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg Weekly Discharges</p>
                <div className="flex items-center gap-2">
                  <p className="text-3xl font-bold">{avgWeeklyDischarges}</p>
                  <ArrowDownRight className="h-5 w-5 text-amber-500" />
                </div>
              </div>
              <UserMinus className="h-10 w-10 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className={`${netChange >= 0 ? 'bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border-blue-200' : 'bg-gradient-to-br from-red-500/10 to-rose-500/10 border-red-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Net Change</p>
                <div className="flex items-center gap-2">
                  <p className="text-3xl font-bold">{netChange >= 0 ? '+' : ''}{netChange}</p>
                  {netChange >= 0 ? 
                    <TrendingUp className="h-5 w-5 text-emerald-500" /> : 
                    <TrendingDown className="h-5 w-5 text-red-500" />
                  }
                </div>
              </div>
              <Activity className="h-10 w-10 text-[#00b0ab]" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/10 border-violet-200 dark:border-violet-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Current Census</p>
                <p className="text-3xl font-bold">{records.length > 0 ? records[0].ending_census || '-' : '-'}</p>
              </div>
              <Users className="h-10 w-10 text-violet-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="program-overview" className="space-y-4">
        <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-5 h-auto">
          <TabsTrigger value="program-overview" className="gap-2" data-testid="tab-program-overview">
            <ClipboardList className="h-4 w-4" /> Program Overview
          </TabsTrigger>
          <TabsTrigger value="report" className="gap-2">
            <Activity className="h-4 w-4" /> Report
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-2">
            <TrendingUp className="h-4 w-4" /> Trends
          </TabsTrigger>
          <TabsTrigger value="records" className="gap-2">
            <Calendar className="h-4 w-4" /> Records
          </TabsTrigger>
          <TabsTrigger value="analysis" className="gap-2">
            <Activity className="h-4 w-4" /> Analysis
          </TabsTrigger>
        </TabsList>

        {/* Program Overview Tab - Centralized metrics entry */}
        <TabsContent value="program-overview" className="space-y-6">
          <Card className="border-[#00b0ab]/30 bg-gradient-to-br from-[#00b0ab]/5 to-transparent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-[#00b0ab]" />
                Program Overview Metrics
              </CardTitle>
              <CardDescription>
                Enter your agency's key performance metrics. This data feeds into the Strategic Plan, Command Center, Dashboard, and all prediction systems.
              </CardDescription>
            </CardHeader>
          </Card>

          {/* Timeframe Selection */}
          <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
            <CardContent className="p-4">
              <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-amber-600" />
                  <span className="font-medium text-amber-800 dark:text-amber-300">Reporting Period</span>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <Select value={periodType} onValueChange={handlePeriodTypeChange} disabled={loadingPeriodData}>
                    <SelectTrigger className="w-32 bg-white dark:bg-slate-900" data-testid="period-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Period End Date:</Label>
                    <Input 
                      type="date" 
                      value={periodEndDate}
                      onChange={(e) => handlePeriodDateChange(e.target.value)}
                      className="w-40 bg-white dark:bg-slate-900"
                      data-testid="period-end-date"
                      disabled={loadingPeriodData}
                    />
                  </div>
                  <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/50 dark:text-amber-200" data-testid="period-indicator">
                    {periodType === 'weekly' ? 'Week ending' : 'Month ending'} {new Date(periodEndDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Badge>
                  {loadingPeriodData && (
                    <div className="flex items-center gap-2 text-amber-700">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm">Loading...</span>
                    </div>
                  )}
                </div>
                
                {/* Period History Dropdown */}
                {periodHistory.length > 0 && (
                  <div className="lg:ml-auto flex items-center gap-2">
                    <Select onValueChange={(val) => {
                      const record = periodHistory.find(r => r.id === val);
                      if (record) handleSelectHistoryPeriod(record);
                    }}>
                      <SelectTrigger className="w-48 bg-white dark:bg-slate-900 border-amber-300" data-testid="period-history-select">
                        <div className="flex items-center gap-2">
                          <History className="h-4 w-4 text-amber-600" />
                          <span className="text-sm">Period History</span>
                        </div>
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b mb-1">
                          Recent Periods with Data
                        </div>
                        {periodHistory.map((record) => (
                          <SelectItem key={record.id} value={record.id} className="cursor-pointer">
                            <div className="flex items-center gap-2">
                              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                              <span>{formatPeriodLabel(record)}</span>
                              <span className="text-xs text-muted-foreground ml-1">
                                ADC: {record.adc}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    {/* Compare Periods Button */}
                    {periodHistory.length >= 2 && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={openCompareDialog}
                        className="bg-white dark:bg-slate-900 border-purple-300 text-purple-700 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                        data-testid="compare-periods-btn"
                      >
                        <GitCompare className="h-4 w-4 mr-1.5" />
                        Compare
                      </Button>
                    )}
                  </div>
                )}
              </div>
              {/* Edit indicator */}
              {isEditingExisting && (
                <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400" data-testid="editing-indicator">
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Editing existing data for this period. Changes will update the existing record.</span>
                </div>
              )}
              {!isEditingExisting && !loadingPeriodData && (
                <div className="mt-3 flex items-center gap-2 text-sm text-blue-700 dark:text-blue-400" data-testid="new-entry-indicator">
                  <Plus className="h-4 w-4" />
                  <span>No data found for this period. Enter new metrics below.</span>
                </div>
              )}
            </CardContent>
          </Card>

          <FormSection title="Census & Admissions" description="Core census and admission statistics" icon={Activity}>
            <FormField label="Current Census" hint="Actual patient count as of today">
              <Input type="number" value={programData.current_census}
                onChange={(e) => setProgramData({ ...programData, current_census: e.target.value })}
                placeholder="e.g., 178" data-testid="input-current-census" />
            </FormField>
            <FormField label="Average Daily Census (ADC)" hint="Target: 178 (Foundation Phase)">
              <Input type="number" step="0.1" value={programData.adc}
                onChange={(e) => setProgramData({ ...programData, adc: e.target.value })}
                placeholder="e.g., 178" data-testid="input-adc" />
            </FormField>
            <FormField label="Total Admissions" hint="Admissions this period">
              <Input type="number" value={programData.total_admissions}
                onChange={(e) => setProgramData({ ...programData, total_admissions: e.target.value })}
                placeholder="e.g., 45" data-testid="input-total-admissions" />
            </FormField>
            <FormField label="Live Discharges (%)" hint="Target: <3%">
              <Input type="number" step="0.1" max="100" value={programData.live_discharges_percent}
                onChange={(e) => setProgramData({ ...programData, live_discharges_percent: e.target.value })}
                placeholder="e.g., 2.5" data-testid="input-live-discharges" />
            </FormField>
            <FormField label="Revocations (%)" hint="Target: <5%">
              <Input type="number" step="0.1" max="100" value={programData.revocations_percent}
                onChange={(e) => setProgramData({ ...programData, revocations_percent: e.target.value })}
                placeholder="e.g., 3.2" data-testid="input-revocations" />
            </FormField>
            <FormField label="Average Length of Stay (days)">
              <Input type="number" step="0.1" value={programData.alos}
                onChange={(e) => setProgramData({ ...programData, alos: e.target.value })}
                placeholder="e.g., 68.5" data-testid="input-alos" />
            </FormField>
            <FormField label="CAHPS Overall Score" hint="Target: ≥85%">
              <Input type="number" step="0.1" max="100" value={programData.cahps_overall_score}
                onChange={(e) => setProgramData({ ...programData, cahps_overall_score: e.target.value })}
                placeholder="e.g., 85.2" data-testid="input-cahps" />
            </FormField>
          </FormSection>

          <FormSection title="Operations Metrics" description="Key operational performance indicators" icon={TrendingUp}>
            <FormField label="Total Referrals" hint="Total referrals received this period">
              <Input type="number" value={programData.total_referrals}
                onChange={(e) => setProgramData({ ...programData, total_referrals: e.target.value })}
                placeholder="e.g., 35" data-testid="input-total-referrals" />
            </FormField>
            <FormField label="Conversion Ratio (%)" hint="Referrals converted to admissions - Target: ≥70%">
              <Input type="number" step="0.1" max="100" value={programData.conversion_ratio}
                onChange={(e) => setProgramData({ ...programData, conversion_ratio: e.target.value })}
                placeholder="e.g., 72.5" data-testid="input-conversion-ratio" />
            </FormField>
            <FormField label="Weekly Discharges" hint="Total discharges this reporting period">
              <Input type="number" value={programData.weekly_discharges}
                onChange={(e) => setProgramData({ ...programData, weekly_discharges: e.target.value })}
                placeholder="e.g., 12" data-testid="input-weekly-discharges" />
            </FormField>
            <FormField label="Median Length of Stay (days)" hint="Median LOS across all patients">
              <Input type="number" step="0.1" value={programData.median_length_of_stay}
                onChange={(e) => setProgramData({ ...programData, median_length_of_stay: e.target.value })}
                placeholder="e.g., 65.0" data-testid="input-median-los" />
            </FormField>
            <FormField label="Net Census Change" hint="Admissions minus Discharges">
              <Input type="number" value={programData.net_census_change}
                onChange={(e) => setProgramData({ ...programData, net_census_change: e.target.value })}
                placeholder="e.g., 5" data-testid="input-net-census-change" />
            </FormField>
          </FormSection>

          <FormSection title="Referral Pipeline" description="Track pending referrals and admission velocity" icon={Clock}>
            <FormField label="Pending Referrals" hint="Referrals awaiting evaluation/decision">
              <Input type="number" value={programData.pending_referrals}
                onChange={(e) => setProgramData({ ...programData, pending_referrals: e.target.value })}
                placeholder="e.g., 8" data-testid="input-pending-referrals" />
            </FormField>
            <FormField label="Same Day Admits" hint="Admissions within 24hrs of referral">
              <Input type="number" value={programData.same_day_admits}
                onChange={(e) => setProgramData({ ...programData, same_day_admits: e.target.value })}
                placeholder="e.g., 5" data-testid="input-same-day-admits" />
            </FormField>
            <FormField label="Insurance Pending" hint="Patients with insurance verification pending">
              <Input type="number" value={programData.insurance_pending}
                onChange={(e) => setProgramData({ ...programData, insurance_pending: e.target.value })}
                placeholder="e.g., 3" data-testid="input-insurance-pending" />
            </FormField>
          </FormSection>

          <FormSection title="Patient Experience" description="Survey and complaint tracking" icon={Target}>
            <FormField label="Open Surveys">
              <Input type="number" value={programData.open_surveys}
                onChange={(e) => setProgramData({ ...programData, open_surveys: e.target.value })}
                placeholder="e.g., 5" data-testid="input-open-surveys" />
            </FormField>
            <FormField label="Resolved Surveys">
              <Input type="number" value={programData.resolved_surveys}
                onChange={(e) => setProgramData({ ...programData, resolved_surveys: e.target.value })}
                placeholder="e.g., 12" data-testid="input-resolved-surveys" />
            </FormField>
            <FormField label="Open Complaints">
              <Input type="number" value={programData.open_complaints}
                onChange={(e) => setProgramData({ ...programData, open_complaints: e.target.value })}
                placeholder="e.g., 2" data-testid="input-open-complaints" />
            </FormField>
            <FormField label="Resolved Complaints">
              <Input type="number" value={programData.resolved_complaints}
                onChange={(e) => setProgramData({ ...programData, resolved_complaints: e.target.value })}
                placeholder="e.g., 8" data-testid="input-resolved-complaints" />
            </FormField>
          </FormSection>

          <div className="flex justify-end pt-4 gap-3">
            {isEditingExisting && (
              <Button 
                variant="outline"
                onClick={() => {
                  setProgramData({
                    adc: '', total_admissions: '', live_discharges_percent: '', revocations_percent: '',
                    alos: '', cahps_overall_score: '', open_surveys: '', resolved_surveys: '',
                    open_complaints: '', resolved_complaints: '', current_census: '',
                    conversion_ratio: '', weekly_discharges: '', median_length_of_stay: '',
                    net_census_change: '', pending_referrals: '', same_day_admits: '',
                    insurance_pending: '', total_referrals: ''
                  });
                  setIsEditingExisting(false);
                  setExistingRecordId(null);
                  toast.info('Form cleared', { description: 'Enter new data for this period.' });
                }}
                data-testid="clear-form-btn"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Clear & Enter New
              </Button>
            )}
            <Button 
              onClick={saveProgramData} 
              disabled={savingProgram || loadingPeriodData}
              className="min-w-40 bg-gradient-to-r from-[#00b0ab] to-[#008a86]"
              data-testid="save-program-btn"
            >
              {savingProgram ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {isEditingExisting ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditingExisting ? 'Update Period Data' : 'Save Program Data'}
                </>
              )}
            </Button>
          </div>

          {/* Data Flow Indicator */}
          <Card className="bg-slate-50 dark:bg-slate-900/50 border-dashed">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-500 mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Data Flows To:</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Dashboard • Strategic Plan • Command Center • AI Intelligence • Workforce Intelligence • Census Predictions • All Reports
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Enhanced Report Tab */}
        <TabsContent value="report">
          <EnhancedReportSection 
            domain="census"
            title="Census Operations Report"
            description="Admissions, discharges, and census trends with AI insights"
            defaultTimeframe="30d"
          />
        </TabsContent>

        {/* Trends */}
        <TabsContent value="trends">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Admissions vs Discharges</CardTitle>
                <CardDescription>Weekly comparison</CardDescription>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="admissions" name="Admissions" fill="#10B981" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="discharges" name="Discharges" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="deaths" name="Deaths" fill="#6B7280" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No data available. Add records to see trends.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Census Trend</CardTitle>
                <CardDescription>Patient census over time</CardDescription>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Area type="monotone" dataKey="census" name="Census" stroke="#4F46E5" fill="#4F46E5" fillOpacity={0.3} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Records */}
        <TabsContent value="records">
          <Card>
            <CardHeader className="space-y-4">
              <div className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Census Records</CardTitle>
                  <CardDescription>Weekly admissions and discharges history</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  {records.length > 0 && (
                    <>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={handleDeleteAll}
                        disabled={deletingAll}
                        className="gap-2"
                        data-testid="delete-all-records-btn"
                      >
                        {deletingAll ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        Delete All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          // Filter records based on current filters before export
                          const filteredForExport = records.filter(r => {
                            if (recordsFilterTeam !== 'All' && r.team !== recordsFilterTeam) return false;
                            if (recordsFilterStartDate && r.period_start < recordsFilterStartDate) return false;
                            if (recordsFilterEndDate && r.period_start > recordsFilterEndDate) return false;
                            return true;
                          });
                          
                          const headers = ['Period Start', 'Period End', 'Team', 'Starting Census', 'Admissions', 'Discharges', 'Deaths', 'Revocations', 'Transfers In', 'Transfers Out', 'Net Change', 'Ending Census', 'Notes', 'Created At'];
                          const csvRows = [headers.join(',')];
                          
                          filteredForExport.forEach(r => {
                            const row = [
                              r.period_start || '',
                              r.period_end || '',
                              r.team || '',
                              r.starting_census || 0,
                              r.admissions || 0,
                              r.discharges || 0,
                              r.deaths || 0,
                              r.revocations || 0,
                              r.transfers_in || 0,
                              r.transfers_out || 0,
                              r.net_change || 0,
                              r.ending_census || 0,
                              `"${(r.notes || '').replace(/"/g, '""')}"`,
                              r.created_at || ''
                            ];
                            csvRows.push(row.join(','));
                          });
                          
                          const csvContent = csvRows.join('\n');
                          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `census_records_${new Date().toISOString().split('T')[0]}.csv`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                          
                          toast.success('Records exported', {
                            description: `${filteredForExport.length} records exported to CSV`
                          });
                        }}
                        className="gap-2"
                        data-testid="export-records-btn"
                      >
                        <FileDown className="h-4 w-4" />
                        Export CSV
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Filter Controls */}
              <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-900 border">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium whitespace-nowrap">Team:</Label>
                  <Select value={recordsFilterTeam} onValueChange={setRecordsFilterTeam}>
                    <SelectTrigger className="w-28 h-8 bg-white dark:bg-slate-800" data-testid="records-filter-team">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TEAMS.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium whitespace-nowrap">From:</Label>
                  <Input 
                    type="date"
                    value={recordsFilterStartDate}
                    onChange={(e) => setRecordsFilterStartDate(e.target.value)}
                    className="w-36 h-8 bg-white dark:bg-slate-800"
                    data-testid="records-filter-start-date"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium whitespace-nowrap">To:</Label>
                  <Input 
                    type="date"
                    value={recordsFilterEndDate}
                    onChange={(e) => setRecordsFilterEndDate(e.target.value)}
                    className="w-36 h-8 bg-white dark:bg-slate-800"
                    data-testid="records-filter-end-date"
                  />
                </div>
                {(recordsFilterTeam !== 'All' || recordsFilterStartDate || recordsFilterEndDate) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setRecordsFilterTeam('All');
                      setRecordsFilterStartDate('');
                      setRecordsFilterEndDate('');
                    }}
                    className="h-8 text-muted-foreground hover:text-foreground"
                    data-testid="records-clear-filters"
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                // Apply filters to records
                const filteredRecords = records.filter(r => {
                  if (recordsFilterTeam !== 'All' && r.team !== recordsFilterTeam) return false;
                  if (recordsFilterStartDate && r.period_start < recordsFilterStartDate) return false;
                  if (recordsFilterEndDate && r.period_start > recordsFilterEndDate) return false;
                  return true;
                });
                
                return (
                  <>
                    {filteredRecords.length !== records.length && (
                      <div className="mb-3 text-sm text-muted-foreground">
                        Showing {filteredRecords.length} of {records.length} records
                      </div>
                    )}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Period</TableHead>
                          <TableHead>Team</TableHead>
                          <TableHead className="text-right">Start</TableHead>
                          <TableHead className="text-right text-emerald-600">Admits</TableHead>
                          <TableHead className="text-right text-amber-600">Discharges</TableHead>
                          <TableHead className="text-right">Deaths</TableHead>
                          <TableHead className="text-right">Net</TableHead>
                          <TableHead className="text-right">End</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredRecords.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                              {records.length === 0 
                                ? 'No records available. Add your first census record.'
                                : 'No records match the selected filters.'}
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredRecords.map((r, idx) => (
                            <TableRow key={r.id || idx}>
                              <TableCell>{r.period_start} to {r.period_end}</TableCell>
                              <TableCell>
                                {r.team ? <Badge variant="outline">{r.team}</Badge> : '-'}
                              </TableCell>
                              <TableCell className="text-right">{r.starting_census}</TableCell>
                              <TableCell className="text-right text-emerald-600 font-medium">+{r.admissions}</TableCell>
                              <TableCell className="text-right text-amber-600 font-medium">-{r.discharges}</TableCell>
                              <TableCell className="text-right">{r.deaths || 0}</TableCell>
                              <TableCell className="text-right">
                                <span className={r.net_change >= 0 ? 'text-emerald-600' : 'text-red-600'}>
                                  {r.net_change >= 0 ? '+' : ''}{r.net_change}
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-semibold">{r.ending_census}</TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(r.id)}
                                  disabled={deletingId === r.id}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-record-${r.id}`}
                                >
                                  {deletingId === r.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-4 w-4" />
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analysis */}
        <TabsContent value="analysis">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Net Change Trend</CardTitle>
                <CardDescription>Weekly net census change</CardDescription>
              </CardHeader>
              <CardContent>
                {chartData.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="net" name="Net Change" stroke="#4F46E5" strokeWidth={2} dot={{ fill: '#4F46E5' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-64 flex items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Summary Statistics</CardTitle>
                <CardDescription>Based on {records.length} periods</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <span>Total Admissions</span>
                    <span className="font-semibold text-emerald-600">{totalAdmissions}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <span>Total Discharges</span>
                    <span className="font-semibold text-amber-600">{totalDischarges}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <span>Avg Weekly Net Change</span>
                    <span className={`font-semibold ${trends?.summary?.total_net_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {records.length > 0 ? (netChange / records.length).toFixed(1) : 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <span>Total Deaths</span>
                    <span className="font-semibold">{records.reduce((s, r) => s + (r.deaths || 0), 0)}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                    <span>Total Revocations</span>
                    <span className="font-semibold">{records.reduce((s, r) => s + (r.revocations || 0), 0)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Compare Periods Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompare className="h-5 w-5 text-purple-600" />
              Compare Periods
            </DialogTitle>
            <DialogDescription>
              Select two periods to compare their metrics side-by-side
            </DialogDescription>
          </DialogHeader>

          {/* Period Selectors */}
          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Period 1 (Baseline)</Label>
              <Select 
                value={comparePeriod1?.id || ''} 
                onValueChange={(val) => setComparePeriod1(periodHistory.find(r => r.id === val))}
              >
                <SelectTrigger data-testid="compare-period1-select">
                  <SelectValue placeholder="Select period..." />
                </SelectTrigger>
                <SelectContent>
                  {periodHistory.map((record) => (
                    <SelectItem key={record.id} value={record.id}>
                      {formatPeriodLabel(record)} - ADC: {record.adc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Period 2 (Compare To)</Label>
              <Select 
                value={comparePeriod2?.id || ''} 
                onValueChange={(val) => setComparePeriod2(periodHistory.find(r => r.id === val))}
              >
                <SelectTrigger data-testid="compare-period2-select">
                  <SelectValue placeholder="Select period..." />
                </SelectTrigger>
                <SelectContent>
                  {periodHistory.map((record) => (
                    <SelectItem key={record.id} value={record.id}>
                      {formatPeriodLabel(record)} - ADC: {record.adc}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Comparison Table */}
          {comparePeriod1 && comparePeriod2 && (
            <div className="border rounded-lg overflow-hidden" data-testid="comparison-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-900">
                    <TableHead className="font-semibold">Metric</TableHead>
                    <TableHead className="text-center font-semibold text-purple-700">
                      {formatPeriodLabel(comparePeriod1)}
                    </TableHead>
                    <TableHead className="text-center w-24">Change</TableHead>
                    <TableHead className="text-center font-semibold text-blue-700">
                      {formatPeriodLabel(comparePeriod2)}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {comparisonMetrics.map((metric) => {
                    const val1 = parseFloat(comparePeriod1[metric.key]) || 0;
                    const val2 = parseFloat(comparePeriod2[metric.key]) || 0;
                    const diff = val2 - val1;
                    const isPositive = diff > 0;
                    const isNegative = diff < 0;
                    const isNeutral = diff === 0;
                    
                    // Determine if positive change is good or bad based on metric
                    const invertedMetrics = ['live_discharges_percent', 'revocations_percent', 'pending_referrals', 'open_complaints'];
                    const isInverted = invertedMetrics.includes(metric.key);
                    const isGood = isInverted ? isNegative : isPositive;
                    const isBad = isInverted ? isPositive : isNegative;

                    return (
                      <TableRow key={metric.key} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <TableCell className="font-medium">{metric.label}</TableCell>
                        <TableCell className="text-center text-purple-700 font-semibold">
                          {val1.toFixed(metric.decimals)}{metric.suffix}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium ${
                            isNeutral ? 'bg-slate-100 text-slate-600' :
                            isGood ? 'bg-emerald-100 text-emerald-700' :
                            isBad ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {isPositive && <ArrowUpRight className="h-3.5 w-3.5" />}
                            {isNegative && <ArrowDownRight className="h-3.5 w-3.5" />}
                            {isNeutral && <Minus className="h-3.5 w-3.5" />}
                            {isPositive ? '+' : ''}{diff.toFixed(metric.decimals)}{metric.suffix}
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-blue-700 font-semibold">
                          {val2.toFixed(metric.decimals)}{metric.suffix}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Summary Cards */}
          {comparePeriod1 && comparePeriod2 && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              <Card className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200">
                <CardContent className="p-3 text-center">
                  <TrendingUp className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-emerald-700">
                    {comparisonMetrics.filter(m => {
                      const diff = (parseFloat(comparePeriod2[m.key]) || 0) - (parseFloat(comparePeriod1[m.key]) || 0);
                      const invertedMetrics = ['live_discharges_percent', 'revocations_percent', 'pending_referrals', 'open_complaints'];
                      return invertedMetrics.includes(m.key) ? diff < 0 : diff > 0;
                    }).length}
                  </p>
                  <p className="text-xs text-emerald-600">Improved</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-50 dark:bg-slate-800/20 border-slate-200">
                <CardContent className="p-3 text-center">
                  <Minus className="h-5 w-5 text-slate-500 mx-auto mb-1" />
                  <p className="text-lg font-bold text-slate-600">
                    {comparisonMetrics.filter(m => {
                      const diff = (parseFloat(comparePeriod2[m.key]) || 0) - (parseFloat(comparePeriod1[m.key]) || 0);
                      return diff === 0;
                    }).length}
                  </p>
                  <p className="text-xs text-slate-500">Unchanged</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
                <CardContent className="p-3 text-center">
                  <TrendingDown className="h-5 w-5 text-red-600 mx-auto mb-1" />
                  <p className="text-lg font-bold text-red-700">
                    {comparisonMetrics.filter(m => {
                      const diff = (parseFloat(comparePeriod2[m.key]) || 0) - (parseFloat(comparePeriod1[m.key]) || 0);
                      const invertedMetrics = ['live_discharges_percent', 'revocations_percent', 'pending_referrals', 'open_complaints'];
                      return invertedMetrics.includes(m.key) ? diff > 0 : diff < 0;
                    }).length}
                  </p>
                  <p className="text-xs text-red-600">Declined</p>
                </CardContent>
              </Card>
            </div>
          )}

          <DialogFooter className="gap-2">
            {comparePeriod1 && comparePeriod2 && (
              <Button 
                variant="outline" 
                onClick={exportComparisonCSV}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="export-comparison-btn"
              >
                <FileDown className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowCompareDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
